/**
 * Data plane — Datasets (L1) API client.
 *
 * Mirrors `backend/src/hub/routes/datasets.py` (ADR-0026). A Dataset is a
 * registered surface over a Source: `internal` (an app data table,
 * auto-registered) or `external` (one table in an external Source). The
 * `/preview` endpoint is the uniform query seam — same shape for both bindings.
 */
import { api } from "./api";

export type DatasetBinding = "internal" | "external";

export interface Dataset {
  id: string;
  name: string;
  description: string | null;
  binding: DatasetBinding;
  /** Physical form (table/view/collection/file/sheet/object/index) — ADR-0027. */
  kind: string;
  source_id: string;
  app_table_id: string | null;
  schema_name: string | null;
  table_name: string | null;
  config: Record<string, unknown>;
  /** Odino visibility (Block 7) — private until published. */
  published: boolean;
  tags: string[];
  /** Grouping label: owning app (internal) or source (external) name. */
  owner: string;
  /** Liveness (ADR-0027 B3): when the schema was last introspected (or null). */
  last_introspected_at: string | null;
  /** The most recent introspect found the schema drifted from its baseline. */
  schema_drifted: boolean;
  /** Freshness stats (ADR-0027 B3.3), best-effort: last measured row count, when
   *  a row was last touched (internal only), and when stats were last refreshed.
   *  All null until first measured (refreshed on re-introspect). */
  row_count: number | null;
  last_updated: string | null;
  stats_checked_at: string | null;
  created_by: string;
  created_at: string;
  updated_at: string | null;
}

/** Create makes an **external** dataset (one table in an external Source). */
export interface DatasetCreate {
  name: string;
  description?: string | null;
  source_id: string;
  schema_name: string;
  table_name: string;
  config?: Record<string, unknown>;
  published?: boolean;
  tags?: string[];
}

/** One always-AND row-filter condition (ADR-0028 N3 "filtered Dataset"). */
export interface DatasetFilterCondition {
  field: string;
  op: string;
  value?: unknown;
}

/** A dataset's row filter — a `{match, conditions}` spec stored on
 *  `config.filter`, validated server-side by `validate_filter_spec`. */
export interface DatasetFilterSpec {
  match: "all" | "any";
  conditions: DatasetFilterCondition[];
}

/** One join ON condition of a federated Dataset (ADR-0063 P5). */
export interface DatasetModelOn {
  left_field: string;
  right_field: string;
  /** The already-joined dataset the left side belongs to (omit ⇒ the primary). */
  left_dataset?: string;
}

/** One persisted federated join (ADR-0063 P5). The default target is another
 *  PUBLISHED global dataset (named by `dataset`); with `source:"entity"` +
 *  `app_id` the target is an APP-TABLE Entity (`dataset` = the app-table name),
 *  resolved by the engine operator-trust. */
export interface DatasetModelJoin {
  dataset: string;
  type: "inner" | "left";
  on: DatasetModelOn[];
  /** `"entity"` ⇒ `dataset` names an app table in `app_id`; omit ⇒ a global dataset. */
  source?: "entity";
  /** The owning app (required when `source === "entity"`). */
  app_id?: string;
}

/** One column mapping of a UNION branch (ADR-0064 P5): the branch's `field` supplies
 *  the primary's `output` column in the stacked result. */
export interface DatasetModelUnionColumn {
  output: string;
  field: string;
}

/** One persisted UNION branch (ADR-0064 P5, DevExpress UnionNode) — STACKS this
 *  branch's rows under the primary's, mapping its columns onto the primary's OUTPUT
 *  columns (no ON, no join type). `union` dedups the combined set, `union_all` keeps
 *  all. Target is a PUBLISHED global dataset, or (with `source:"entity"` + `app_id`)
 *  an app-table Entity. Cross-source works — the engine materialises + concatenates. */
export interface DatasetModelUnion {
  dataset: string;
  type: "union" | "union_all";
  columns: DatasetModelUnionColumn[];
  source?: "entity";
  app_id?: string;
}

/** One persisted UNFOLD (ADR-0064 P5 Transformation, DevExpress TransformationNode) —
 *  EXPLODES a JSON-array column into one row per element. An OBJECT element hoists its
 *  keys onto the row; a SCALAR element keeps the field's name. `keep_empty` (default
 *  true) keeps a row once with the field NULL for an empty array instead of dropping it. */
export interface DatasetModelUnfold {
  field: string;
  keep_empty?: boolean;
}

/** A dataset's persisted QUERY MODEL — the join graph (+ shape + unions + unfold). Same
 *  source ⇒ the engine pushes down one native JOIN; different sources ⇒ it federates
 *  automatically. Stored on `config.model`, validated server-side by
 *  `validate_dataset_model`. (The shape fields — select/filters/group_by/aggregations/
 *  order_by/search — ride alongside `joins` in the persisted object; the QueryBuilder
 *  authors them.) */
export interface DatasetModelSpec {
  joins: DatasetModelJoin[];
  unions?: DatasetModelUnion[];
  unfold?: DatasetModelUnfold[];
}

export type DatasetUpdate = Partial<{
  name: string;
  description: string | null;
  published: boolean;
  tags: string[];
  schema_name: string;
  table_name: string;
  /** ADR-0028 N3 — set the always-AND row filter, or `null` to clear it. */
  filter: DatasetFilterSpec | null;
  /** ADR-0063 P5 — set the federated joins, or `{}` / `null` to clear. */
  model: DatasetModelSpec | Record<string, never> | null;
}>;

export interface DatasetFieldInfo {
  name: string;
  type: string;
  /** Human label / meaning from the L2 semantic layer (null when not annotated). */
  label?: string | null;
  semantic_type?: string | null;
}

export interface DatasetPreview {
  rows: Record<string, unknown>[];
  total: number;
  page: number;
  page_size: number;
}

export interface DatasetPreviewRequest {
  page?: number;
  page_size?: number;
  filters?: Record<string, unknown>[];
  sort?: Record<string, unknown>[];
  search?: string;
}

export const datasetsApi = {
  list: (params?: { binding?: DatasetBinding; published?: boolean; tag?: string[] }) =>
    api.get<Dataset[]>("/datasets", { query: params }),

  get: (id: string) => api.get<Dataset>(`/datasets/${id}`),

  create: (body: DatasetCreate) => api.post<Dataset>("/datasets", body),

  update: (id: string, body: DatasetUpdate) => api.patch<Dataset>(`/datasets/${id}`, body),

  remove: (id: string) => api.delete<void>(`/datasets/${id}`),

  fields: (id: string) => api.get<DatasetFieldInfo[]>(`/datasets/${id}/fields`),

  preview: (id: string, body: DatasetPreviewRequest = {}) =>
    api.post<DatasetPreview>(`/datasets/${id}/preview`, body),

  /** Re-introspect the live schema → drift verdict + refreshed fingerprint (B3). */
  introspect: (id: string) => api.post<IntrospectResult>(`/datasets/${id}/introspect`, {}),

  /**
   * Build-time federation hint (ADR-0064 P4): does a browse over this dataset's
   * model run live (`light`) or fall back to the capped materialise (`heavy`)?
   * A non-blocking advisory for the QueryBuilder badge — never runs the query.
   */
  federationHint: (id: string) =>
    api.get<FederationHint>(`/datasets/${id}/federation-hint`),
};

export interface FederationHint {
  level: "light" | "heavy";
  reasons: string[];
  approx_rows: number | null;
}

export interface IntrospectResult {
  drifted: boolean;
  field_count: number;
  fingerprint: string;
  last_introspected_at: string;
  /** Freshness stats refreshed by the same re-introspect (B3.3), best-effort. */
  row_count: number | null;
  last_updated: string | null;
}
